import React, { useState, useRef, useEffect } from 'react'
import HandleMarkup from '@src/components/HandleMarkup'
import { Link } from '@src/components/Link'
import { IconButton } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { CaretDownIcon, CaretUpIcon } from '@src/icons'
import clsx from 'clsx'

const useStyles = makeStyles<{ isPdp?: boolean }>()((theme, params) => ({
  container: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  button: {
    padding: theme.spacing(0.5),
    alignSelf: 'flex-end',
  },
  caret: {
    fontSize: params?.isPdp
      ? theme.typography.pxToRem(12)
      : theme.typography.pxToRem(9),
    fontWeight: theme.typography.fontWeightLight,
    color: theme.palette.primary.main,
  },
  displayedText: {
    wordBreak: 'break-word',
    overflow: 'hidden',
    display: '-webkit-box',
    '-webkit-box-orient': 'vertical',
    '-webkit-line-clamp': '3',
  },
  pdpDisplayedText: {
    wordBreak: 'break-word',
    overflow: 'hidden',
    display: '-webkit-box',
    '-webkit-box-orient': 'vertical',
    '-webkit-line-clamp': '4',
  },
  expandedText: {
    wordBreak: 'break-word',
    overflow: 'visible',
    display: 'block',
    '-webkit-line-clamp': 'unset',
  },
}))

interface MapSrpAttributeValuesProps {
  values: string[]
  className?: string | ''
  url?: string
  isPdp?: boolean
}

const AttrComponent = (props) =>
  props.href ? (
    <Link href={props.href}>
      <a className={props.className}>{props.value}</a>
    </Link>
  ) : (
    <HandleMarkup value={props.value} className={props.className} />
  )

const MapAttributeValues: React.FC<MapSrpAttributeValuesProps> = ({
  values,
  className,
  url,
  isPdp = false,
}) => {
  const { classes } = useStyles({ isPdp: !!isPdp })
  const [isExpanded, setIsExpanded] = useState(false)
  const [showToggle, setShowToggle] = useState(false)
  const textRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = textRef.current
    if (!el) return
    setShowToggle(el.scrollHeight > el.clientHeight)
  }, [values])

  const toggleExpansion = () => {
    setIsExpanded(!isExpanded)
  }

  if (!Array.isArray(values) || values.length === 0) {
    return <span className={className}>&mdash;</span>
  }

  if (values.length > 1) {
    const joinedValues = values.join(', ')
    return (
      <div className={classes.container}>
        <div
          ref={textRef}
          className={clsx({
            [classes.expandedText]: isExpanded,
            [classes.pdpDisplayedText]: !isExpanded && isPdp,
            [classes.displayedText]: !isExpanded && !isPdp,
          })}
        >
          <AttrComponent value={joinedValues} href={url} />
        </div>
        {showToggle && (
          <IconButton
            onClick={toggleExpansion}
            size="small"
            className={classes.button}
            aria-label={isExpanded ? 'Show less' : 'Show more'}
          >
            {isExpanded ? (
              <CaretUpIcon className={classes.caret} />
            ) : (
              <CaretDownIcon className={classes.caret} />
            )}
          </IconButton>
        )}
      </div>
    )
  }

  return <AttrComponent value={values[0]} className={className} href={url} />
}

export default MapAttributeValues
